<template>
    <card :title="pageTitle">
        <div v-if="loading" class="text-center py-4">
            <b-spinner /> Loading batch details...
        </div>

        <div v-else-if="batch">
            <!-- Batch Info Section -->
            <b-card class="mb-4">
                <b-card-title>Batch Information</b-card-title>
                <b-row>
                    <b-col cols="6">
                        <p class="mb-1"><strong>Compound:</strong> {{ batch.compound?.name }}</p>
                        <p class="mb-1"><strong>Batch Number:</strong> {{ batch.batchNumber }}</p>
                        <p class="mb-1"><strong>Batch Size:</strong> {{ batch.batchSize }} {{ batch.compound?.unitOfMeasure }}</p>
                        <p class="mb-1">
                            <strong>Package Size:</strong>
                            {{ batch.packageSize ? `${batch.packageSize} ${batch.compound?.unitOfMeasure}` : 'N/A' }}
                        </p>
                    </b-col>
                    <b-col cols="6">
                        <p class="mb-1"><strong>Lot Number:</strong> {{ batch.lotNumber || 'N/A' }}</p>
                        <p class="mb-1"><strong>Beyond Use Date:</strong> {{ batch.beyondUseDate ? formatDate(batch.beyondUseDate) : 'N/A' }}</p>
                        <p class="mb-1">
                            <strong>Mode:</strong>
                            <b-badge :variant="batch.rxNumber ? 'info' : 'primary'">
                                {{ batch.rxNumber ? 'Prescription' : 'Batch' }}
                            </b-badge>
                        </p>
                        <p class="mb-1" v-if="batch.rxNumber">
                            <strong>Rx Number:</strong> {{ batch.rxNumber }}-{{ batch.rfNumber }}
                        </p>
                        <p class="mb-1"><strong>Created:</strong> {{ formatDateTime(batch.created) }} by {{ batch.createdBy }}</p>
                    </b-col>
                </b-row>
            </b-card>

            <!-- Ingredients Section -->
            <b-card v-if="batch.ingredients && batch.ingredients.length > 0" class="mb-4">
                <b-card-title>Ingredients Used</b-card-title>
                <b-table-simple hover small bordered>
                    <b-thead>
                        <b-tr>
                            <b-th>Ingredient</b-th>
                            <b-th>Quantity</b-th>
                            <b-th>Lot Number</b-th>
                            <b-th>Expiration Date</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="ingredient in batch.ingredients" :key="ingredient.id">
                            <b-td>{{ ingredient.drugName || 'Unknown' }}</b-td>
                            <b-td>{{ ingredient.quantity.toFixed(2) }} {{ ingredient.unitOfMeasure }}</b-td>
                            <b-td>{{ ingredient.lotNumber || 'Not recorded' }}</b-td>
                            <b-td>{{ ingredient.expirationDate ? formatDate(ingredient.expirationDate) : 'Not recorded' }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-card>

            <!-- Steps Section -->
            <b-card class="mb-4">
                <b-card-title>
                    Preparation Steps
                    <small class="text-muted ml-2">
                        ({{ verifiedStepCount }} / {{ requiredStepCount }} required verifications completed)
                    </small>
                </b-card-title>

                <div v-if="!batch.steps || batch.steps.length === 0">
                    <p class="text-muted">No preparation steps recorded for this batch.</p>
                </div>

                <div v-else>
                    <div v-for="step in batch.steps" :key="step.id" class="mb-3">
                        <b-card :border-variant="getStepBorderVariant(step)" body-class="p-3">
                            <div class="d-flex align-items-start">
                                <div class="mr-3">
                                    <b-badge variant="primary" pill class="p-2">
                                        Step {{ step.stepNumber }}
                                    </b-badge>
                                </div>
                                <div class="flex-grow-1">
                                    <h6>{{ step.description }}</h6>

                                    <!-- Weight Information -->
                                    <div v-if="step.expectedWeight || step.actualWeight" class="mt-2">
                                        <b-row>
                                            <b-col cols="4">
                                                <small class="text-muted">Expected Weight:</small><br />
                                                <strong v-if="step.expectedWeight">{{ step.expectedWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure }}</strong>
                                                <span v-else class="text-muted">N/A</span>
                                            </b-col>
                                            <b-col cols="4">
                                                <small class="text-muted">Actual Weight:</small><br />
                                                <strong v-if="step.actualWeight">{{ step.actualWeight.toFixed(2) }} {{ batch.compound?.unitOfMeasure }}</strong>
                                                <span v-else class="text-muted">Not recorded</span>
                                            </b-col>
                                            <b-col cols="4">
                                                <small class="text-muted">Tolerance Status:</small><br />
                                                <b-badge v-if="step.actualWeight != null && step.withinTolerance !== null"
                                                    :variant="step.withinTolerance ? 'success' : 'danger'">
                                                    {{ step.withinTolerance ? 'Within Tolerance' : 'Out of Tolerance' }}
                                                </b-badge>
                                                <span v-else class="text-muted">N/A</span>
                                            </b-col>
                                        </b-row>
                                    </div>

                                    <!-- Photo -->
                                    <div v-if="step.photoId" class="mt-3">
                                        <small class="text-muted">Photo Documentation:</small><br />
                                        <img v-if="shouldLoadImage(step.photoId)"
                                            :src="getImageUrl(step.photoId)"
                                            alt="Step Photo"
                                            class="img-thumbnail mt-2"
                                            style="max-width: 400px; cursor: pointer;"
                                            @click="showImageModal(step.photoId)"
                                            @error="onImageError(step.photoId)" />
                                        <div v-else class="mt-2">
                                            <b-button size="sm" variant="outline-primary" @click="loadImage(step.photoId)">
                                                <i class="fas fa-image" /> Load Photo
                                            </b-button>
                                        </div>
                                    </div>

                                    <!-- Completion Info -->
                                    <div v-if="step.completed" class="mt-2 pt-2 border-top">
                                        <small class="text-muted">
                                            <i class="fas fa-check-circle text-success" />
                                            Completed on {{ formatDateTime(step.completed) }} by {{ step.completedBy }}
                                        </small>
                                    </div>
                                </div>

                                <!-- Pharmacist Verification Toggle -->
                                <div v-if="step.requirePharmacistSignOff" class="ml-3 text-center" style="min-width: 120px;">
                                    <small class="text-muted d-block mb-1">Pharmacist Sign-Off</small>
                                    <b-form-checkbox
                                        :checked="step.pharmacistVerified === true"
                                        switch
                                        size="lg"
                                        @change="toggleStepVerification(step, $event)"
                                        :disabled="verifyingStepId === step.id" />
                                    <div v-if="step.pharmacistVerified && step.pharmacistVerifiedBy" class="mt-1">
                                        <small class="text-success">
                                            <i class="fas fa-check" /> {{ step.pharmacistVerifiedBy }}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </div>
                </div>
            </b-card>

            <!-- Action Buttons -->
            <b-card>
                <div class="d-flex justify-content-between">
                    <b-button variant="outline-secondary" @click="goBack">
                        <i class="fas fa-arrow-left" /> Back to Queue
                    </b-button>
                    <div class="d-flex" style="gap: 8px;">
                        <b-button
                            variant="danger"
                            @click="showRejectModal = true"
                            :disabled="submitting">
                            <i class="fas fa-times-circle" /> Reject
                        </b-button>
                        <b-button
                            variant="success"
                            @click="approveBatch"
                            :disabled="!canApprove || submitting">
                            <b-spinner v-if="submitting" small class="mr-1" />
                            <i v-else class="fas fa-check-circle" /> Approve & Post Inventory
                        </b-button>
                    </div>
                </div>
                <div v-if="!canApprove && requiredStepCount > 0" class="mt-2 text-right">
                    <small class="text-warning">
                        <i class="fas fa-exclamation-triangle" />
                        All required step verifications must be completed before approving.
                    </small>
                </div>
            </b-card>
        </div>

        <b-alert v-else variant="warning" show>
            Batch not found or not in Awaiting Verification status.
        </b-alert>

        <!-- Image Modal -->
        <b-modal v-model="showImageModalFlag" title="Step Photo" size="xl" hide-footer>
            <img v-if="selectedImageId" :src="getImageUrl(selectedImageId)" alt="Step Photo" class="img-fluid" />
        </b-modal>

        <!-- Reject Modal -->
        <b-modal v-model="showRejectModal" title="Reject Batch" @ok="rejectBatch" ok-variant="danger" ok-title="Reject">
            <p>How would you like to handle this batch?</p>
            <b-form-group>
                <b-form-radio v-model="rejectSendBack" :value="true">
                    <strong>Send back for rework</strong>
                    <br /><small class="text-muted">Returns the batch to In Progress so the technician can make corrections.</small>
                </b-form-radio>
                <b-form-radio v-model="rejectSendBack" :value="false" class="mt-2">
                    <strong>Reject permanently</strong>
                    <br /><small class="text-muted">Marks the batch as Rejected. Ingredients already deducted will not be restored.</small>
                </b-form-radio>
            </b-form-group>
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { CompoundBatch, CompoundBatchSteps } from '@/models/Drug/CompoundDrug';
import Axios from 'axios';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "CompoundBatchVerificationDetailsPage"
})
export default class CompoundBatchVerificationDetailsPage extends Vue {
    private batch: CompoundBatch | null = null;
    private loading = false;
    private submitting = false;
    private verifyingStepId: number | null = null;
    private showImageModalFlag = false;
    private selectedImageId: number | null = null;
    private loadedImages: number[] = [];
    private imageUrls: { [key: number]: string } = {};
    private showRejectModal = false;
    private rejectSendBack = true;

    get pageTitle(): string {
        if (this.batch) {
            return `Verify Batch: ${this.batch.batchNumber}`;
        }
        return 'Compound Batch Verification';
    }

    get requiredStepCount(): number {
        return this.batch?.steps?.filter(s => s.requirePharmacistSignOff).length || 0;
    }

    get verifiedStepCount(): number {
        return this.batch?.steps?.filter(s => s.requirePharmacistSignOff && s.pharmacistVerified === true).length || 0;
    }

    get canApprove(): boolean {
        if (!this.batch) return false;
        if (this.requiredStepCount === 0) return true;
        return this.verifiedStepCount >= this.requiredStepCount;
    }

    async mounted() {
        const batchId = this.$route.params.batchId;
        if (batchId) {
            await this.loadBatch(parseInt(batchId));
        }
    }

    async loadBatch(batchId: number) {
        this.loading = true;
        try {
            const response = await Axios.get<CompoundBatch>(`/CompoundBatch/${batchId}`);
            this.batch = response.data;
        } catch (error) {
            console.error('Error loading batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    async toggleStepVerification(step: CompoundBatchSteps, verified: boolean) {
        if (!this.batch) return;
        this.verifyingStepId = step.id;

        try {
            await Axios.post(`/CompoundBatch/${this.batch.id}/step/${step.id}/verify`, { verified });
            step.pharmacistVerified = verified;
            if (verified) {
                step.pharmacistVerifiedBy = 'You';
                step.pharmacistVerifiedDate = new Date();
            } else {
                step.pharmacistVerifiedBy = null;
                step.pharmacistVerifiedDate = null;
            }
        } catch (error) {
            console.error('Error verifying step:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.verifyingStepId = null;
        }
    }

    async approveBatch() {
        if (!this.batch || !this.canApprove) return;
        this.submitting = true;

        try {
            await Axios.post(`/CompoundBatch/${this.batch.id}/verify`);
            this.$notification(NotificationOptions.success(`Batch ${this.batch.batchNumber} has been verified and inventory has been posted.`));
            this.$router.push({ name: 'CompoundBatchVerificationQueue' });
        } catch (error) {
            console.error('Error verifying batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.submitting = false;
        }
    }

    async rejectBatch() {
        if (!this.batch) return;
        this.submitting = true;

        try {
            await Axios.post(`/CompoundBatch/${this.batch.id}/reject`, {
                sendBackToInProgress: this.rejectSendBack
            });

            const message = this.rejectSendBack
                ? `Batch ${this.batch.batchNumber} has been sent back for rework.`
                : `Batch ${this.batch.batchNumber} has been rejected.`;

            this.$notification(NotificationOptions.success(message));
            this.$router.push({ name: 'CompoundBatchVerificationQueue' });
        } catch (error) {
            console.error('Error rejecting batch:', error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.submitting = false;
        }
    }

    goBack() {
        this.$router.push({ name: 'CompoundBatchVerificationQueue' });
    }

    getStepBorderVariant(step: CompoundBatchSteps): string {
        if (!step.requirePharmacistSignOff) return 'secondary';
        if (step.pharmacistVerified === true) return 'success';
        return 'warning';
    }

    // Image handling methods
    shouldLoadImage(photoId: number): boolean {
        return this.loadedImages.includes(photoId);
    }

    async loadImage(photoId: number) {
        if (this.loadedImages.includes(photoId)) return;

        try {
            const response = await Axios.get(`/Image/${photoId}`, { responseType: 'blob' });
            const imageUrl = URL.createObjectURL(response.data);
            this.$set(this.imageUrls, photoId, imageUrl);
            this.loadedImages.push(photoId);
        } catch (error) {
            console.error(`Failed to load image ${photoId}:`, error);
            this.$notification(NotificationOptions.error(`Failed to load image ${photoId}`));
        }
    }

    getImageUrl(photoId: number): string {
        return this.imageUrls[photoId] || '';
    }

    showImageModal(photoId: number) {
        this.selectedImageId = photoId;
        this.showImageModalFlag = true;
        this.loadImage(photoId);
    }

    onImageError(photoId: number) {
        console.error(`Failed to load image with ID: ${photoId}`);
    }

    formatDate(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    }

    formatDateTime(dateString: string | Date | null): string {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    }

    beforeDestroy() {
        Object.values(this.imageUrls).forEach(url => URL.revokeObjectURL(url));
    }
}
</script>

<style scoped>
.border-top {
    border-top: 1px solid #dee2e6;
}
</style>
